import type { Branding } from "../api/types";

/** Built-in defaults — must mirror the `:root` block in leaderboard/src/public.rs PAGE_CSS so the
 *  dashboard preview matches the real public page. */
export const BRAND_DEFAULTS = {
  accent: "#3fb950",
  bg: "#0e1116",
  card: "#171b22",
  text: "#e6edf3",
  dim: "#8b949e",
  row: "#1c212a",
  highlight: "#243042",
  border: "#262c36",
  font: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  radius: "14px",
} as const;

type ColorKey = "accent" | "bg" | "card" | "text" | "dim" | "row" | "highlight" | "border";

const COLOR_FIELDS: { key: ColorKey; label: string }[] = [
  { key: "accent", label: "Accent" },
  { key: "bg", label: "Background" },
  { key: "card", label: "Card" },
  { key: "text", label: "Text" },
  { key: "dim", label: "Muted text" },
  { key: "row", label: "Row stripe" },
  { key: "highlight", label: "Highlight row" },
  { key: "border", label: "Border" },
];

/** Resolve an effective value: this level → inherited (project) → built-in default. */
function eff<K extends keyof typeof BRAND_DEFAULTS>(
  b: Branding,
  inherited: Branding | undefined,
  key: K,
): string {
  return (b[key] || inherited?.[key] || BRAND_DEFAULTS[key]) as string;
}

/** Is `s` something `<input type=color>` can show (a #rrggbb)? Used to drive the swatch picker. */
function asHex(s: string | undefined): string {
  return s && /^#[0-9a-fA-F]{6}$/.test(s) ? s : "#000000";
}

export interface BrandingEditorProps {
  value: Branding;
  onChange: (b: Branding) => void;
  /** Project-level branding, shown as the inherited fallback (board editor only). */
  inherited?: Branding;
  /** Label for the inherited source, e.g. "project default". */
  inheritedLabel?: string;
}

/** Shared branding form + live preview. Used by the board editor (per-leaderboard branding) and
 *  project Settings (project-default branding). Editing is non-destructive: an empty field means
 *  "inherit", so we delete the key rather than store "". */
export function BrandingEditor({ value, onChange, inherited, inheritedLabel }: BrandingEditorProps) {
  function set<K extends keyof Branding>(key: K, v: string) {
    const next = { ...value };
    if (v.trim() === "") delete next[key];
    else next[key] = v as Branding[K];
    onChange(next);
  }

  const ph = (key: keyof typeof BRAND_DEFAULTS) =>
    inherited?.[key] || (BRAND_DEFAULTS[key] as string);

  return (
    <div className="brand-editor" style={{ display: "grid", gap: 18, gridTemplateColumns: "minmax(0,1fr) 280px" }}>
      <div className="form-grid" style={{ display: "grid", gap: 14 }}>
        <div className="field full">
          <label>Logo URL</label>
          <input
            className="input mono"
            placeholder={inherited?.logo_url || "https://…/logo.png"}
            value={value.logo_url ?? ""}
            onChange={(e) => set("logo_url", e.target.value)}
          />
        </div>
        <div className="field full">
          <label>Title override</label>
          <input
            className="input"
            placeholder={inherited?.title || "(uses the board's display name)"}
            value={value.title ?? ""}
            onChange={(e) => set("title", e.target.value)}
          />
        </div>

        <div className="field full">
          <label>Colors {inheritedLabel && <span className="faint">— blank inherits the {inheritedLabel}</span>}</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 10 }}>
            {COLOR_FIELDS.map(({ key, label }) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="color"
                  aria-label={`${label} swatch`}
                  value={asHex(value[key] || inherited?.[key] || BRAND_DEFAULTS[key])}
                  onChange={(e) => set(key, e.target.value)}
                  style={{ width: 34, height: 34, padding: 0, border: "none", background: "none", cursor: "pointer", flex: "0 0 auto" }}
                />
                <input
                  className="input mono"
                  style={{ flex: 1 }}
                  placeholder={ph(key)}
                  value={value[key] ?? ""}
                  onChange={(e) => set(key, e.target.value)}
                  aria-label={label}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="field">
          <label>Font family</label>
          <input
            className="input mono"
            placeholder={ph("font")}
            value={value.font ?? ""}
            onChange={(e) => set("font", e.target.value)}
          />
        </div>
        <div className="field">
          <label>Corner radius</label>
          <input
            className="input mono"
            placeholder={ph("radius")}
            value={value.radius ?? ""}
            onChange={(e) => set("radius", e.target.value)}
          />
        </div>

        <div className="field full">
          <label>Custom CSS <span className="faint">— escape hatch, applied last</span></label>
          <textarea
            className="input mono"
            rows={4}
            placeholder=".lb-title { letter-spacing: 1px }"
            value={value.custom_css ?? ""}
            onChange={(e) => set("custom_css", e.target.value)}
          />
        </div>
      </div>

      {/* Live preview — mirrors the public page layout/vars. */}
      <BrandingPreview value={value} inherited={inherited} />
    </div>
  );
}

function BrandingPreview({ value, inherited }: { value: Branding; inherited?: Branding }) {
  const v = (k: keyof typeof BRAND_DEFAULTS) => eff(value, inherited, k);
  const title = value.title || inherited?.title || "Your Leaderboard";
  const logo = value.logo_url || inherited?.logo_url;
  const rows = [
    { rank: "🥇", name: "Ada", score: "12,840" },
    { rank: "🥈", name: "Lin", score: "11,002" },
    { rank: "🥉", name: "Kai", score: "9,640" },
    { rank: "4", name: "Sam", score: "8,113" },
  ];
  return (
    <div className="field" style={{ alignSelf: "start" }}>
      <label>Live preview</label>
      <div
        style={{
          background: v("bg"),
          padding: 16,
          borderRadius: 12,
          fontFamily: v("font"),
        }}
      >
        <div style={{ background: v("card"), border: `1px solid ${v("border")}`, borderRadius: v("radius"), overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: `1px solid ${v("border")}` }}>
            {logo && <img src={logo} alt="" style={{ height: 20, borderRadius: 4 }} />}
            <span style={{ color: v("text"), fontWeight: 700, fontSize: 15, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
            <span style={{ color: v("accent"), fontSize: 10 }}>live</span>
          </div>
          <div style={{ padding: 6 }}>
            {rows.map((r, i) => (
              <div key={r.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, background: i % 2 === 0 ? v("row") : "transparent" }}>
                <span style={{ width: 26, textAlign: "center", fontWeight: 700, color: v("dim") }}>{r.rank}</span>
                <span style={{ flex: 1, color: v("text") }}>{r.name}</span>
                <span style={{ fontWeight: 700, color: v("accent") }}>{r.score}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <span className="hint">Approximate — the live page also applies your custom CSS.</span>
    </div>
  );
}
